import type { ReactNode } from 'react'
import React from 'react'

import type { AuthRole } from '@fourthwall/auth'
import type { Paths } from '@fourthwall/types/dist/String/Paths'
import { MOBILE_APP_ANDROID_LINK, MOBILE_APP_IOS_LINK } from '@fourthwall/utils/dist/links'

import { ForbiddenRouteState } from '../../common/ForbiddenRouteState'
import type { DashboardPermissions } from './types'
import { useDashboardPermissions } from './useDashboardPermissions'

type RoleRouteProps =
  | {
      permission: Paths<DashboardPermissions> | Paths<DashboardPermissions>[]
      showForbiddenState?: boolean
      element: ReactNode
    }
  | {
      roleName: AuthRole
      showForbiddenState?: boolean
      element: ReactNode
    }

export const PermissionGuardedRoute = (props: RoleRouteProps) => {
  const { showForbiddenState = true } = props
  const { hasPermission, hasAnyPermission, hasRole, hasGroup } = useDashboardPermissions()

  const hasAccess = () => {
    if ('permission' in props) {
      return hasPermission(props.permission)
    }

    if ('roleName' in props) {
      return hasRole(props.roleName)
    }

    return false
  }

  if (!hasAccess() && !showForbiddenState) {
    return null
  }

  // ThankYou sander doesn't have dashboard access, can only send ThankYous via mobile app
  if (!hasAccess() && !hasAnyPermission() && hasGroup('THANK_YOU')) {
    return (
      <ForbiddenRouteState
        title="No dashboard access"
        subtitle={
          <>
            Your role permits you only to send thank you messages via mobile app. Download
            Fourthwall app for{' '}
            <a href={MOBILE_APP_IOS_LINK} target="_blank" rel="noreferrer">
              iOS
            </a>{' '}
            or{' '}
            <a href={MOBILE_APP_ANDROID_LINK} target="_blank" rel="noreferrer">
              Android
            </a>
          </>
        }
      />
    )
  }

  if (!hasAccess()) {
    return <ForbiddenRouteState />
  }

  return props.element
}
